import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  FiShoppingCart, 
  FiTruck, 
  FiPackage, 
  FiFileText, 
  FiCheckCircle, 
  FiClock, 
  FiXCircle,
  FiPlus,
  FiSearch,
  FiFilter,
  FiDownload,
  FiArrowRight,
  FiDollarSign,
  FiCalendar,
  FiUser,
  FiHash,
  FiBox,
  FiAlertCircle,
  FiRefreshCw,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiSave,
  FiPercent,
  FiTag,
  FiUserCheck,
  FiUserX
} from 'react-icons/fi';
import { listSupplier } from '../../actions/supplierAction';
import { listAllPurchaseOrders, getPurchaseOrderById, createPurchaseOrder, updatePurchaseOrderStatus } from '../../actions/purchaseOrderActions';
import { fetchProducts } from '../../actions/POS/productAction';
import { clearPoPrefillData } from '../../actions/uiActions';
import purchaseOrderService from '../../services/purchaseOrderService';
import Breadcrumb from '../../components/common/Breadcrumb';

const PurchaseOrder = () => {
  const { darkMode, poPrefillData } = useSelector((state) => state.ui);
  const dispatch = useDispatch();
  
  // Get suppliers from Redux
  const { suppliers = [] } = useSelector((state) => state.supplierList);
  
  // Get products from Redux (POS products)
  const { allProducts: products = [], loading: productsLoading } = useSelector((state) => state.products);
  
  // Get purchase orders from Redux
  const { loading: poLoading, purchaseOrders = [], error: poError } = useSelector((state) => state.poList);
  const { loading: poDetailLoading, purchaseOrder: selectedPO } = useSelector((state) => state.poDetails);
  const { loading: poCreating, success: poCreateSuccess, error: poCreateError } = useSelector((state) => state.poCreate || {});
  
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isNewPOModalOpen, setIsNewPOModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newItem, setNewItem] = useState({ product: '', productCode: '', quantity: 1, price: 0 });
  const [isCreatingPO, setIsCreatingPO] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [showAlert, setShowAlert] = useState(false);
  const [currentPO, setCurrentPO] = useState({
    supplier: '',
    orderDate: new Date().toISOString().split('T')[0],
    expectedDate: '',
    paymentTerms: 'Net 30',
    shippingAddress: '',
    notes: '',
    discount: 0,
    tax: 0
  });

  const showAlertMessage = (message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  };

  const getAlertBgColor = () => {
    switch (alertType) {
      case 'success': return 'bg-green-100 border-green-300 dark:bg-green-900/70 dark:border-green-700';
      case 'error': return 'bg-red-100 border-red-300 dark:bg-red-900/70 dark:border-red-700';
      case 'warning': return 'bg-yellow-100 border-yellow-300 dark:bg-yellow-900/70 dark:border-yellow-700';
      case 'info': return 'bg-blue-100 border-blue-300 dark:bg-blue-900/70 dark:border-blue-700';
      default: return 'bg-gray-100 border-gray-300 dark:bg-gray-900/70 dark:border-gray-700';
    }
  };

  const getAlertTextColor = () => {
    switch (alertType) {
      case 'success': return 'text-green-800 dark:text-green-200';
      case 'error': return 'text-red-800 dark:text-red-200';
      case 'warning': return 'text-yellow-800 dark:text-yellow-200';
      case 'info': return 'text-blue-800 dark:text-blue-200';
      default: return 'text-gray-800 dark:text-gray-200';
    }
  };

  const getAlertIcon = () => {
    switch (alertType) {
      case 'success': return <FiCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />;
      case 'error': return <FiX className="w-5 h-5 text-red-600 dark:text-red-400" />;
      case 'warning': return <FiAlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />;
      case 'info': return <FiShoppingCart className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
      default: return <FiShoppingCart className="w-5 h-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  // Load suppliers, products, and purchase orders on component mount
  useEffect(() => {
    dispatch(listSupplier());
    dispatch(fetchProducts());
    dispatch(listAllPurchaseOrders());
  }, [dispatch]);

  // Handle prefill data from ROA Reorder button (via Redux)
  useEffect(() => {
    if (poPrefillData && suppliers.length > 0) {
      const supplier = suppliers.find(s => 
        s.SUP_CODE?.toString() === poPrefillData.supplierId?.toString() ||
        s.SUP_NAME === poPrefillData.supplierName
      );
      
      if (supplier) {
        setSelectedSupplier(supplier);
        setCurrentPO(prev => ({
          ...prev,
          supplier: supplier.SUP_NAME,
          shippingAddress: supplier.SUP_ADDRESS
        }));
      }
      
      if (poPrefillData.productCode && poPrefillData.productName) {
        setProductSearchTerm(poPrefillData.productName);
        setSelectedProduct({
          id: poPrefillData.productCode,
          name: poPrefillData.productName,
          price: poPrefillData.purchasePrice || 0
        });
        setNewItem({
          product: poPrefillData.productName,
          productCode: poPrefillData.productCode,
          quantity: 1,
          price: poPrefillData.purchasePrice || 0
        });
      }
      
      setIsNewPOModalOpen(true);
      dispatch(clearPoPrefillData());
    }
  }, [poPrefillData, suppliers, dispatch]);

  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    p.id?.toString().includes(productSearchTerm) ||
    p.sku?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    p.barcode?.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  const filteredOrders = purchaseOrders.filter(order => {
    const poId = `PO-${order.poId}`;
    const matchesSearch = poId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (order.supplierName || '').toLowerCase().includes(searchTerm.toLowerCase());
    const statusLabel = purchaseOrderService.getStatusLabel(order.status);
    const matchesStatus = statusFilter === 'all' || statusLabel.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const activeSuppliers = suppliers.filter(s => s.SUP_STATUS === 'A');

  const stats = {
    total: purchaseOrders.length,
    pending: purchaseOrders.filter(o => o.status === 'P').length,
    received: purchaseOrders.filter(o => o.status === 'R').length,
    cancelled: purchaseOrders.filter(o => o.status === 'C').length,
    totalValue: purchaseOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0)
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const handleViewPO = (poId) => {
    dispatch(getPurchaseOrderById(poId));
    setIsViewModalOpen(true);
  };

  const toggleStatusMenu = (poId) => {
    setStatusMenuOpen((prev) => (prev === poId ? null : poId));
  };

  const handleChangePOStatus = async (poId, status) => {
    try {
      setUpdatingStatusId(poId);
      await dispatch(updatePurchaseOrderStatus(poId, status));
      setStatusMenuOpen(null);
      const statusText = status === 'R' ? 'Received' : status === 'P' ? 'Pending' : 'Cancelled';
      showAlertMessage(`Purchase Order status updated to ${statusText}!`, 'success');
    } catch (error) {
      console.error('Failed to update PO status', error);
      showAlertMessage(error?.message || 'Failed to update PO status', 'error');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleRefresh = () => {
    dispatch(listAllPurchaseOrders());
    showAlertMessage('Purchase Orders refreshed!', 'info');
  };

  const getStatusColor = (status) => {
    const colors = purchaseOrderService.getStatusColor(status);
    return `${colors.bg} ${colors.text}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'P': return <FiClock className="w-3 h-3" />;
      case 'R': return <FiCheckCircle className="w-3 h-3" />;
      case 'C': return <FiXCircle className="w-3 h-3" />;
      default: return <FiFileText className="w-3 h-3" />;
    }
  };

  const calculateTotals = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);
    const discountAmount = (subtotal * currentPO.discount) / 100;
    const taxAmount = ((subtotal - discountAmount) * currentPO.tax) / 100;
    const total = subtotal - discountAmount + taxAmount;
    return { subtotal, discountAmount, taxAmount, total };
  };

  const addItemToOrder = () => {
    if (!selectedProduct || newItem.quantity <= 0 || newItem.price <= 0) {
      showAlertMessage('Please select a product and enter valid quantity and price', 'error');
      return;
    }
    
    setOrderItems([...orderItems, {
      id: Date.now(),
      product: selectedProduct.name,
      productCode: selectedProduct.id,
      sku: selectedProduct.sku || selectedProduct.id,
      quantity: newItem.quantity,
      price: newItem.price,
      total: newItem.quantity * newItem.price
    }]);
    
    setNewItem({ product: '', productCode: '', quantity: 1, price: 0 });
    setSelectedProduct(null);
    setProductSearchTerm('');
    setShowProductDropdown(false);
    showAlertMessage('Item added to order!', 'success');
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setProductSearchTerm(product.name);
    setNewItem({
      ...newItem,
      product: product.name,
      productCode: product.id,
      price: parseFloat(product.price) || 0
    });
    setShowProductDropdown(false);
  };

  const removeItem = (id) => {
    setOrderItems(orderItems.filter(item => item.id !== id));
  };

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setCurrentPO({
      ...currentPO,
      supplier: supplier.SUP_NAME,
      shippingAddress: supplier.SUP_ADDRESS
    });
    setShowSupplierDropdown(false);
  };

  const handleCreatePO = async () => {
    if (!selectedSupplier || orderItems.length === 0) {
      showAlertMessage('Please select a supplier and add items to create a purchase order', 'error');
      return;
    }

    if (!currentPO.orderDate) {
      showAlertMessage('Please select an order date', 'error');
      return;
    }
    
    setIsCreatingPO(true);
    
    try {
      const poData = {
        supplierId: selectedSupplier.SUP_CODE,
        orderDate: currentPO.orderDate,
        items: orderItems.map(item => ({
          productCode: item.productCode,
          quantity: item.quantity,
          price: item.price
        }))
      };
      
      await dispatch(createPurchaseOrder(poData));
      
      setSelectedSupplier(null);
      setOrderItems([]);
      setSelectedProduct(null);
      setProductSearchTerm('');
      setCurrentPO({
        supplier: '',
        orderDate: new Date().toISOString().split('T')[0],
        expectedDate: '',
        paymentTerms: 'Net 30',
        shippingAddress: '',
        notes: '',
        discount: 0,
        tax: 0
      });
      setIsNewPOModalOpen(false);
      showAlertMessage('Purchase Order created successfully!', 'success');
    } catch (error) {
      console.error('Error creating PO:', error);
      showAlertMessage('Failed to create Purchase Order. Please try again.', 'error');
    } finally {
      setIsCreatingPO(false);
    }
  };

  const flowSteps = [
    { icon: FiFileText, title: 'Create PO', description: 'Admin/Manager creates Purchase Order', color: 'bg-blue-500' },
    { icon: FiTruck, title: 'Send to Supplier', description: 'PO is sent to the supplier', color: 'bg-blue-500' },
    { icon: FiPackage, title: 'Receive Items', description: 'Supplier delivers the items', color: 'bg-blue-500' },
    { icon: FiBox, title: 'Update Stock', description: 'Inventory is updated', color: 'bg-blue-500' },
    { icon: FiCheckCircle, title: 'Complete', description: 'PO status changes to Received', color: 'bg-blue-500' },
  ];

  return (
    <div className={`flex flex-col p-1 md:p-1 rounded-xl shadow-md h-full ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border`}>
      
      {/* Alert Message */}
      {showAlert && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] animate-fade-in-down w-full max-w-md px-2 sm:px-0">
          <div className={`flex items-center justify-between p-3 sm:p-4 rounded-xl shadow-lg border ${getAlertBgColor()} ${getAlertTextColor()} mx-2`}>
            <div className="flex items-center gap-2 sm:gap-3">
              {getAlertIcon()}
              <p className="font-medium text-sm sm:text-base">{alertMessage}</p>
            </div>
            <button onClick={() => setShowAlert(false)} className="hover:opacity-70 transition-opacity">
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <Breadcrumb current="Purchase Orders" />

      {/* Header */}
      <div className="mt-2 mb-3 md:mb-5">
        <div className="flex items-center justify-between mb-1 md:mb-2">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow">
              <FiShoppingCart className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">Purchase Orders</h1>
              <p className="text-xs text-gray-600 dark:text-gray-400">Manage supplier purchase orders and track deliveries</p>
            </div>
          </div>
          <button
            onClick={() => setIsNewPOModalOpen(true)}
            className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm"
          >
            <FiPlus className="w-4 h-4" /> New Purchase Order
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-1 md:gap-2 mb-1 md:mb-2">
          <div className={`rounded-lg p-2 shadow border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Orders</p>
                <p className="text-sm font-bold text-blue-600 dark:text-blue-400">{stats.total}</p>
              </div>
              <FiFileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>

          <div className={`rounded-lg p-2 shadow border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Pending</p>
                <p className="text-sm font-bold text-yellow-600 dark:text-yellow-400">{stats.pending}</p>
              </div>
              <FiClock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>

          <div className={`rounded-lg p-2 shadow border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Received</p>
                <p className="text-sm font-bold text-green-600 dark:text-green-400">{stats.received}</p>
              </div>
              <FiCheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
          </div>

          <div className={`rounded-lg p-2 shadow border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Cancelled</p>
                <p className="text-sm font-bold text-red-600 dark:text-red-400">{stats.cancelled}</p>
              </div>
              <FiXCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
          </div>

          <div className={`rounded-lg p-2 shadow border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Value</p>
                <p className="text-sm font-bold text-blue-600 dark:text-blue-400">Rs.{stats.totalValue.toLocaleString()}</p>
              </div>
              <FiDollarSign className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-3 border-b border-gray-200 dark:border-gray-700">
          {['overview', 'orders'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1.5 text-sm font-medium transition-all border-b-2 -mb-px ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab === 'overview' ? 'Overview' : 'All Orders'}
            </button>
          ))}
        </div>

        {/* Search and Filters - Only for orders tab */}
        {activeTab === 'orders' && (
          <div className="flex flex-col md:flex-row gap-1 md:gap-2 mb-1 md:mb-2">
            <div className="relative flex-1">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by PO number or supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-8 pr-3 py-2 text-sm rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
              />
            </div>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-2 py-1.5 text-xs rounded-lg font-medium ${statusFilter === 'all' ? 'bg-blue-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}
              >
                All
              </button>
              <button
                onClick={() => setStatusFilter('pending')}
                className={`px-2 py-1.5 text-xs rounded-lg font-medium ${statusFilter === 'pending' ? 'bg-blue-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}
              >
                Pending
              </button>
              <button
                onClick={() => setStatusFilter('received')}
                className={`px-2 py-1.5 text-xs rounded-lg font-medium ${statusFilter === 'received' ? 'bg-blue-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}
              >
                Received
              </button>
              <button
                onClick={() => setStatusFilter('cancelled')}
                className={`px-2 py-1.5 text-xs rounded-lg font-medium ${statusFilter === 'cancelled' ? 'bg-blue-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}
              >
                Cancelled
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="flex-grow overflow-y-auto mb-1 md:mb-2">
        <div className={`rounded-lg p-1 md:p-2 h-full overflow-y-auto ${darkMode ? 'bg-gray-700/30' : 'bg-gray-100'}`}>
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-3">
             

              {/* Why Purchase Orders are Important
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50' : 'bg-white'} border ${darkMode ? 'border-gray-600' : 'border-gray-200'} shadow-sm`}>
                <h3 className="text-base font-bold mb-3 dark:text-white flex items-center gap-2">
                  <FiCheckCircle className="text-blue-500" />
                  Why Purchase Orders are Important in POS
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { icon: FiBox, title: 'Manage Inventory', desc: 'Helps manage stock in and replenishment', color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30' },
                    { icon: FiTruck, title: 'Track Purchases', desc: 'Tracks all supplier purchases', color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30' },
                    { icon: FiAlertCircle, title: 'Prevent Errors', desc: 'Prevents over-ordering', color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30' },
                    { icon: FiFileText, title: 'Accounting & Audit', desc: 'Used for accounting purposes', color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30' },
                    { icon: FiCheckCircle, title: 'Match Invoices', desc: 'Matches invoice vs received goods', color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30' },
                    { icon: FiDollarSign, title: 'Budget Control', desc: 'Helps control spending', color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30' },
                  ].map((item, idx) => (
                    <div key={idx} className={`p-2 rounded-lg ${darkMode ? 'bg-gray-600/50' : 'bg-gray-50'} flex items-start gap-2`}>
                      <div className={`p-1.5 rounded-lg ${item.color}`}>
                        <item.icon className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <p className="text-xs font-medium dark:text-white">{item.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div> */}

              {/* Recent Orders */}
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50' : 'bg-white'} border ${darkMode ? 'border-gray-600' : 'border-gray-200'} shadow-sm`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-bold dark:text-white">Recent Purchase Orders</h3>
                  <div className="flex items-center gap-2">
                    <button onClick={handleRefresh} className={`p-1.5 rounded-lg ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'} transition-colors`}>
                      <FiRefreshCw className={`w-3.5 h-3.5 ${poLoading ? 'animate-spin' : ''} text-gray-500`} />
                    </button>
                    <button onClick={() => setActiveTab('orders')} className="text-blue-600 dark:text-blue-400 text-xs font-medium hover:underline flex items-center gap-1">
                      View All <FiArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                
                {poLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="relative">
                      <div className={`w-6 h-6 border-3 rounded-full animate-spin ${darkMode ? 'border-blue-800' : 'border-blue-200'}`}></div>
                      <div className="absolute inset-0 w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <span className="ml-2 text-xs text-gray-500">Loading...</span>
                  </div>
                ) : purchaseOrders.length === 0 ? (
                  <div className="text-center py-6">
                    <FiPackage className="w-8 h-8 mx-auto mb-2 opacity-50 text-gray-400" />
                    <p className="text-xs text-gray-500">No purchase orders found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`text-left text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <tr>
                          <th className="pb-2 font-medium">PO Number</th>
                          <th className="pb-2 font-medium">Supplier</th>
                          <th className="pb-2 font-medium text-center">Items</th>
                          <th className="pb-2 font-medium text-right">Amount</th>
                          <th className="pb-2 font-medium text-center">Status</th>
                          <th className="pb-2 font-medium text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                        {purchaseOrders.slice(0, 5).map((order) => (
                          <tr key={order.poId} className="hover:bg-gray-50 dark:hover:bg-gray-600/50 transition-colors">
                            <td className="py-2 text-xs font-medium text-blue-600 dark:text-blue-400">PO-{order.poId}</td>
                            <td className="py-2">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-medium text-xs">
                                  {(order.supplierName || 'S').charAt(0)}
                                </div>
                                <span className="text-xs dark:text-gray-300">{order.supplierName || 'Unknown'}</span>
                              </div>
                            </td>
                            <td className="py-2 text-center text-xs dark:text-gray-300">{order.items?.length || 0}</td>
                            <td className="py-2 text-right text-xs font-medium dark:text-white">Rs.{(order.totalAmount || 0).toLocaleString()}</td>
                            <td className="py-2 text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                                {getStatusIcon(order.status)}
                                {purchaseOrderService.getStatusLabel(order.status)}
                              </span>
                            </td>
                            <td className="py-2 text-center">
                              <button onClick={() => handleViewPO(order.poId)} className="text-blue-600 dark:text-blue-400 hover:underline text-xs font-medium flex items-center justify-center gap-1 mx-auto">
                                <FiEye className="w-3 h-3" /> View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* PO Flow Diagram */}
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700/50' : 'bg-white'} border ${darkMode ? 'border-gray-600' : 'border-gray-200'} shadow-sm`}>
                <h3 className="text-base font-bold mb-3 dark:text-white text-center">Purchase Order Flow in POS</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {flowSteps.map((step, idx) => (
                    <div key={idx} className="text-center">
                      <div className={`p-2 ${step.color} rounded-lg shadow-lg mb-1 inline-block mx-auto`}>
                        <step.icon className="w-4 h-4 text-white" />
                      </div>
                      <h4 className="text-xs font-bold dark:text-white">{step.title}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{step.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className={`rounded-xl border overflow-hidden h-full ${darkMode ? 'bg-gray-700/30 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className="overflow-x-auto h-full">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full">
                    <thead className={`sticky top-0 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-semibold tracking-wider">PO #</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold tracking-wider">Supplier</th>
                        <th className="px-2 py-2 text-center text-xs font-semibold tracking-wider hidden sm:table-cell">Items</th>
                        <th className="px-2 py-2 text-right text-xs font-semibold tracking-wider">Amount</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold tracking-wider hidden md:table-cell">Ordered</th>
                        <th className="px-2 py-2 text-center text-xs font-semibold tracking-wider">Status</th>
                        <th className="px-2 py-2 text-center text-xs font-semibold tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {poLoading ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center">
                            <div className="flex items-center justify-center">
                              <div className="relative">
                                <div className={`w-6 h-6 border-3 rounded-full animate-spin ${darkMode ? 'border-blue-800' : 'border-blue-200'}`}></div>
                                <div className="absolute inset-0 w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                              </div>
                              <span className="ml-2 text-xs text-gray-500">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : filteredOrders.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center">
                            <FiPackage className="w-8 h-8 mx-auto mb-2 opacity-50 text-gray-400" />
                            <p className="text-xs text-gray-500">No purchase orders found</p>
                          </td>
                        </tr>
                      ) : (
                        filteredOrders.map((order) => (
                          <tr key={order.poId} className={`transition-colors duration-150 ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'}`}>
                            <td className="px-2 py-2 whitespace-nowrap">
                              <span className="text-xs font-medium text-blue-600 dark:text-blue-400">PO-{order.poId}</span>
                            </td>
                            <td className="px-2 py-2 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-medium text-xs flex-shrink-0">
                                  {(order.supplierName || 'S').charAt(0)}
                                </div>
                                <span className="text-xs dark:text-gray-300 truncate max-w-[120px]">{order.supplierName || 'Unknown'}</span>
                              </div>
                             </td>
                            <td className="px-2 py-2 whitespace-nowrap text-center hidden sm:table-cell">
                              <span className="text-xs">{order.items?.length || 0}</span>
                             </td>
                            <td className="px-2 py-2 whitespace-nowrap text-right">
                              <span className="text-xs font-medium dark:text-white">Rs.{(order.totalAmount || 0).toLocaleString()}</span>
                             </td>
                            <td className="px-2 py-2 whitespace-nowrap text-left hidden md:table-cell">
                              <span className="text-xs dark:text-gray-300">{formatDate(order.orderDate)}</span>
                             </td>
                            <td className="px-2 py-2 whitespace-nowrap text-center">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                                {getStatusIcon(order.status)}
                                {purchaseOrderService.getStatusLabel(order.status)}
                              </span>
                             </td>
                            <td className="px-2 py-2 whitespace-nowrap text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => handleViewPO(order.poId)} className="p-1 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 transition-colors" title="View">
                                  <FiEye className="w-3.5 h-3.5" />
                                </button>
                                <div className="relative">
                                  <button onClick={() => toggleStatusMenu(order.poId)} className="p-1 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 transition-colors" title="Change Status">
                                    <FiEdit2 className="w-3.5 h-3.5" />
                                  </button>
                                  {statusMenuOpen === order.poId && (
                                    <div className={`absolute right-0 mt-1 w-28 rounded-md shadow-lg z-50 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                      <div className="p-1">
                                        <button onClick={() => handleChangePOStatus(order.poId, 'R')} disabled={updatingStatusId === order.poId} className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                          <span className="text-green-600 dark:text-green-400">Received</span>
                                        </button>
                                        <button onClick={() => handleChangePOStatus(order.poId, 'P')} disabled={updatingStatusId === order.poId} className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                          <span className="text-yellow-600 dark:text-yellow-400">Pending</span>
                                        </button>
                                        <button onClick={() => handleChangePOStatus(order.poId, 'C')} disabled={updatingStatusId === order.poId} className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                          <span className="text-red-600 dark:text-red-400">Cancelled</span>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                             </td>
                            </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Purchase Order Modal */}
      {isNewPOModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-2">
          <div className={`rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="relative">
              <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                    <FiShoppingCart className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-lg font-bold">Create New Purchase Order</h2>
                </div>
                <button onClick={() => setIsNewPOModalOpen(false)} className={`p-1 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                  <FiX className="w-4 h-4 text-gray-400" />
                </button>
              </div>

              <div className="p-4">
                {/* Supplier Selection */}
                <div className="mb-4">
                  <label className="block text-xs font-medium mb-1">Select Supplier *</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowSupplierDropdown(!showSupplierDropdown)}
                      className={`w-full px-3 py-2 rounded-lg border text-sm flex items-center justify-between ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                    >
                      <div className="flex items-center gap-2">
                        {selectedSupplier ? (
                          <>
                            <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-medium text-xs">
                              {selectedSupplier.SUP_NAME.charAt(0)}
                            </div>
                            <div className="text-left">
                              <div className="text-xs font-medium">{selectedSupplier.SUP_NAME}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">ID: {selectedSupplier.SUP_CODE}</div>
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-gray-500">Select a supplier...</span>
                        )}
                      </div>
                      {showSupplierDropdown ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
                    </button>
                    
                    {showSupplierDropdown && (
                      <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 max-h-48 overflow-y-auto">
                        {activeSuppliers.map((supplier) => (
                          <div key={supplier.SUP_CODE} onClick={() => handleSupplierSelect(supplier)} className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b dark:border-gray-700 last:border-b-0">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-medium text-xs">
                                {supplier.SUP_NAME.charAt(0)}
                              </div>
                              <div>
                                <div className="text-xs font-medium dark:text-white">{supplier.SUP_NAME}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{supplier.SUP_PHONENO}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Items Table */}
                <div className="mb-4">
                  <label className="block text-xs font-medium mb-1">Order Items *</label>
                  <div className={`rounded-lg border ${darkMode ? 'border-gray-600' : 'border-gray-200'} overflow-hidden`}>
                    <table className="w-full text-xs">
                      <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <tr>
                          <th className="px-2 py-2 text-left">Product</th>
                          <th className="px-2 py-2 text-center w-16">Qty</th>
                          <th className="px-2 py-2 text-right w-24">Price</th>
                          <th className="px-2 py-2 text-right w-24">Total</th>
                          <th className="px-2 py-2 text-center w-12">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-gray-700">
                        {orderItems.map((item) => (
                          <tr key={item.id}>
                            <td className="px-2 py-2 dark:text-gray-300">{item.product}</td>
                            <td className="px-2 py-2 text-center dark:text-gray-300">{item.quantity}</td>
                            <td className="px-2 py-2 text-right dark:text-gray-300">Rs.{item.price.toLocaleString()}</td>
                            <td className="px-2 py-2 text-right font-medium dark:text-white">Rs.{item.total.toLocaleString()}</td>
                            <td className="px-2 py-2 text-center">
                              <button onClick={() => removeItem(item.id)} className="text-red-600 dark:text-red-400">
                                <FiTrash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {orderItems.length === 0 && (
                          <tr>
                            <td colSpan="5" className="px-2 py-6 text-center text-xs text-gray-500">No items added</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Add Item Form */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 p-3 rounded-lg border dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 mb-4">
                  <div className="relative">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-0.5">Product</label>
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearchTerm}
                      onChange={(e) => { setProductSearchTerm(e.target.value); setShowProductDropdown(true); if (!e.target.value) setSelectedProduct(null); }}
                      onFocus={() => setShowProductDropdown(true)}
                      className={`w-full px-2 py-1.5 rounded-lg border text-xs ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                    {showProductDropdown && productSearchTerm && (
                      <div className="absolute z-50 mt-1 w-full max-h-36 overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700">
                        {filteredProducts.slice(0, 8).map((product) => (
                          <div key={product.id} onClick={() => handleProductSelect(product)} className="px-2 py-1.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 border-b dark:border-gray-700">
                            <div className="text-xs font-medium dark:text-white">{product.name}</div>
                            <div className="text-xs text-gray-500">Price: Rs.{parseFloat(product.price || 0).toLocaleString()}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-0.5">Quantity</label>
                    <input type="number" min="1" value={newItem.quantity} onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })} className={`w-full px-2 py-1.5 rounded-lg border text-xs ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-0.5">Price</label>
                    <input type="number" min="0" step="0.01" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })} className={`w-full px-2 py-1.5 rounded-lg border text-xs ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                  </div>
                  <div className="flex items-end">
                    <button type="button" onClick={addItemToOrder} className="w-full px-2 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-xs flex items-center justify-center gap-1">
                      <FiPlus className="w-3 h-3" /> Add Item
                    </button>
                  </div>
                </div>

                {/* Order Details */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-xs font-medium mb-0.5">Order Date</label>
                    <input type="date" value={currentPO.orderDate} onChange={(e) => setCurrentPO({ ...currentPO, orderDate: e.target.value })} className={`w-full px-2 py-1.5 rounded-lg border text-xs ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-0.5">Expected Delivery Date</label>
                    <input type="date" value={currentPO.expectedDate} onChange={(e) => setCurrentPO({ ...currentPO, expectedDate: e.target.value })} className={`w-full px-2 py-1.5 rounded-lg border text-xs ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium mb-0.5">Shipping Address</label>
                    <input type="text" placeholder="Shipping address" value={currentPO.shippingAddress} onChange={(e) => setCurrentPO({ ...currentPO, shippingAddress: e.target.value })} className={`w-full px-2 py-1.5 rounded-lg border text-xs ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
                  </div>
                </div>

                {/* Summary */}
                <div className={`p-3 rounded-lg border ${darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}>
                  <h3 className="text-sm font-bold mb-2 dark:text-white">Order Summary</h3>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between"><span>Subtotal</span><span>Rs.{calculateTotals().subtotal.toLocaleString()}</span></div>
                    <div className="flex justify-between items-center">
                      <span>Discount</span>
                      <div className="flex items-center gap-1"><input type="number" min="0" max="100" value={currentPO.discount} onChange={(e) => setCurrentPO({ ...currentPO, discount: parseFloat(e.target.value) || 0 })} className="w-12 px-1 py-0.5 rounded border dark:border-gray-600 dark:bg-gray-700 text-right text-xs" /><span>%</span></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Tax</span>
                      <div className="flex items-center gap-1"><input type="number" min="0" max="100" value={currentPO.tax} onChange={(e) => setCurrentPO({ ...currentPO, tax: parseFloat(e.target.value) || 0 })} className="w-12 px-1 py-0.5 rounded border dark:border-gray-600 dark:bg-gray-700 text-right text-xs" /><span>%</span></div>
                    </div>
                    <div className="border-t dark:border-gray-600 pt-2 mt-2">
                      <div className="flex justify-between"><span className="text-sm font-bold">Total</span><span className="text-base font-bold text-blue-600 dark:text-blue-400">Rs.{calculateTotals().total.toLocaleString()}</span></div>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={handleCreatePO} disabled={!selectedSupplier || orderItems.length === 0 || isCreatingPO} className={`flex-1 py-2 rounded-lg font-medium text-xs flex items-center justify-center gap-1 ${!selectedSupplier || orderItems.length === 0 || isCreatingPO ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'}`}>
                      {isCreatingPO ? <><FiRefreshCw className="w-3 h-3 animate-spin" /> Creating...</> : <><FiSave className="w-3 h-3" /> Create PO</>}
                    </button>
                    <button onClick={() => setIsNewPOModalOpen(false)} className="px-3 py-2 rounded-lg font-medium text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600">Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View PO Details Modal */}
      {isViewModalOpen && selectedPO && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 p-2">
          <div className={`relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`sticky top-0 flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-center gap-2">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow">
                  <FiFileText className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold dark:text-white">Purchase Order Details</h2>
                  <p className="text-xs text-gray-500">PO-{selectedPO.poId}</p>
                </div>
              </div>
              <button onClick={() => setIsViewModalOpen(false)} className={`p-1 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <FiX className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {poDetailLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="relative">
                    <div className={`w-6 h-6 border-3 rounded-full animate-spin ${darkMode ? 'border-blue-800' : 'border-blue-200'}`}></div>
                    <div className="absolute inset-0 w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-blue-50'}`}>
                      <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 mb-0.5"><FiTruck className="w-3 h-3" /><span className="text-xs font-medium">Supplier</span></div>
                      <p className="text-xs font-bold dark:text-white">{selectedPO.supplierName || 'N/A'}</p>
                    </div>
                    <div className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-green-50'}`}>
                      <div className="flex items-center gap-1 text-green-600 dark:text-green-400 mb-0.5"><FiCalendar className="w-3 h-3" /><span className="text-xs font-medium">Order Date</span></div>
                      <p className="text-xs font-bold dark:text-white">{formatDate(selectedPO.orderDate)}</p>
                    </div>
                    <div className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-blue-50'}`}>
                      <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 mb-0.5"><FiDollarSign className="w-3 h-3" /><span className="text-xs font-medium">Total Amount</span></div>
                      <p className="text-xs font-bold dark:text-white">Rs.{(selectedPO.totalAmount || 0).toLocaleString()}</p>
                    </div>
                    <div className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-purple-50'}`}>
                      <div className="flex items-center gap-1 text-purple-600 dark:text-purple-400 mb-0.5"><FiCheckCircle className="w-3 h-3" /><span className="text-xs font-medium">Status</span></div>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedPO.status)}`}>
                        {getStatusIcon(selectedPO.status)}
                        {purchaseOrderService.getStatusLabel(selectedPO.status)}
                      </span>
                    </div>
                  </div>

                  <div className={`rounded-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'} overflow-hidden`}>
                    <div className={`px-3 py-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <h3 className="text-xs font-semibold dark:text-white flex items-center gap-1"><FiPackage className="w-3 h-3" /> Order Items ({selectedPO.items?.length || 0})</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className={`${darkMode ? 'bg-gray-700/50' : 'bg-gray-50/50'}`}>
                          <tr className="text-left">
                            <th className="px-2 py-2 font-medium">Product</th>
                            <th className="px-2 py-2 font-medium">Code</th>
                            <th className="px-2 py-2 font-medium text-right">Qty</th>
                            <th className="px-2 py-2 font-medium text-right">Price</th>
                            <th className="px-2 py-2 font-medium text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                          {selectedPO.items?.map((item, idx) => (
                            <tr key={idx}>
                              <td className="px-2 py-2 dark:text-gray-300">{item.productName}</td>
                              <td className="px-2 py-2 dark:text-gray-400">{item.productCode}</td>
                              <td className="px-2 py-2 text-right">{parseInt(item.qtyOrdered)}</td>
                              <td className="px-2 py-2 text-right">Rs.{(item.unitPrice || 0).toLocaleString()}</td>
                              <td className="px-2 py-2 text-right font-medium">Rs.{((parseInt(item.qtyOrdered) || 0) * (item.unitPrice || 0)).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} font-medium`}>
                          <tr>
                            <td colSpan="4" className="px-2 py-2 text-right">Grand Total:</td>
                            <td className="px-2 py-2 text-right text-blue-600 dark:text-blue-400 font-bold">Rs.{(selectedPO.totalAmount || 0).toLocaleString()}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={() => setIsViewModalOpen(false)} className={`px-3 py-1.5 rounded-lg font-medium text-xs ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Close</button>
                    <button className="px-3 py-1.5 rounded-lg font-medium text-xs bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 flex items-center gap-1"><FiDownload className="w-3 h-3" /> Export PDF</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrder;